import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Close,
  Fullscreen,
  FullscreenExit,
  NavigateBefore,
  NavigateNext,
  ZoomIn,
  ZoomOut,
  CenterFocusStrong,
  RestartAlt
} from '@mui/icons-material';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { signal, effect } from '@preact/signals-react';
import useDraggableResizable, { type DragStart, type ResizeStart } from 'app/hooks/useDraggableResizable';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

// Constants
const DEFAULT_PDF_SIZE = { width: 55, height: 70 };
const MOBILE_BREAKPOINT = 900;

// Helper function to calculate centered position
const calculateCenterPosition = (size: { width: number; height: number }) => {
  const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
  if (!isMobile) {
    return {
      x: (window.innerWidth - (window.innerWidth * size.width) / 100) / 2,
      y: (window.innerHeight - (window.innerHeight * size.height) / 100) / 2
    };
  }
  return { x: 0, y: 0 };
};

// Persistent signals for PDF viewer state (survives modal open/close)
const pdfPosition = signal({ x: 0, y: 0 });
const pdfSize = signal(DEFAULT_PDF_SIZE);
const pdfScale = signal(1.0);
const pdfIsFullscreen = signal(false);
const pdfPageNumber = signal(1);
const pdfNumPages = signal<number | null>(null);
const pdfCurrentUrl = signal<string | null>(null);
const pdfIsOpen = signal(false);
const pdfIsInitialized = signal(false);

// Auto-reset page values when URL changes
effect(() => {
  const url = pdfCurrentUrl.value;
  if (url) {
    // Reset page navigation for new document
    pdfPageNumber.value = 1;
    pdfNumPages.value = null;
  }
});

// Auto-initialize position when opening for first time
effect(() => {
  if (pdfIsOpen.value && !pdfIsInitialized.value && pdfPosition.value.x === 0 && pdfPosition.value.y === 0) {
    pdfPosition.value = calculateCenterPosition(pdfSize.value);
    pdfIsInitialized.value = true;
  }
});

export interface PDFOverlayProps {
  open: boolean;
  pdfUrl: string | null;
  onClose: () => void;
}

const PDFOverlay: React.FC<PDFOverlayProps> = ({ open, pdfUrl, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync props with signals
  useEffect(() => {
    pdfIsOpen.value = open && !!pdfUrl;
    if (open && pdfUrl && pdfCurrentUrl.value !== pdfUrl) {
      pdfCurrentUrl.value = pdfUrl;
      setIsLoading(true);
      setError(null);
    }
  }, [open, pdfUrl]);

  const handleToggleFullscreen = () => {
    if (!pdfIsFullscreen.value) {
      // Store current position and size for restoration
      const prevPosition = { ...pdfPosition.value };
      const prevSize = { ...pdfSize.value };

      // Maximize to viewport
      pdfPosition.value = { x: 0, y: 0 };
      pdfSize.value = { width: 100, height: 100 };
      pdfIsFullscreen.value = true;

      // Store previous state for restoration
      (window as any).pdfPrevState = { position: prevPosition, size: prevSize };
    } else {
      // Restore previous position and size
      const prevState = (window as any).pdfPrevState;
      if (prevState) {
        pdfPosition.value = prevState.position;
        pdfSize.value = prevState.size;
        delete (window as any).pdfPrevState;
      }
      pdfIsFullscreen.value = false;
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      if (pdfIsFullscreen.value) {
        handleToggleFullscreen();
      } else {
        onClose();
      }
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      previousPage();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      nextPage();
    } else if (event.key === '+' || event.key === '=') {
      event.preventDefault();
      zoomIn();
    } else if (event.key === '-') {
      event.preventDefault();
      zoomOut();
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    pdfNumPages.value = numPages;
    // Only reset to page 1 if current page is beyond the document or if no page is set
    if (pdfPageNumber.value > numPages || pdfPageNumber.value < 1) {
      pdfPageNumber.value = 1;
    }
    setIsLoading(false);
    setError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('PDF loading error:', error);
    setError('Failed to load PDF document');
    setIsLoading(false);
  };

  const changePage = (offset: number) => {
    const newPageNumber = pdfPageNumber.value + offset;
    pdfPageNumber.value = Math.max(1, Math.min(pdfNumPages.value || 1, newPageNumber));
  };

  const previousPage = () => changePage(-1);
  const nextPage = () => changePage(1);

  const zoomIn = () => {
    pdfScale.value = Math.min(pdfScale.value + 0.1, 3.0);
  };
  const zoomOut = () => {
    pdfScale.value = Math.max(pdfScale.value - 0.1, 0.5);
  };

  const resetZoomOnly = () => {
    pdfScale.value = 1.0;
  };

  const resetToDefault = () => {
    pdfScale.value = 1.0;
    pdfSize.value = DEFAULT_PDF_SIZE;
    pdfPosition.value = calculateCenterPosition(DEFAULT_PDF_SIZE);
  };

  const handleDragMove = useCallback((event: MouseEvent, start: DragStart) => {
    const dialogWidth = (window.innerWidth * pdfSize.value.width) / 100;

    const newX = event.clientX - start.x;
    const newY = event.clientY - start.y;

    // Ensure at least 60px of title bar is visible at the top
    const minY = -50; // Allow only 60px above viewport (negative Y moves up)
    const maxY = window.innerHeight - 50;
    const minX = -dialogWidth + 100; // Keep some content visible
    const maxX = window.innerWidth - 100;

    pdfPosition.value = {
      x: Math.max(minX, Math.min(maxX, newX)),
      y: Math.max(minY, Math.min(maxY, newY))
    };
  }, []);

  const handleResizeMove = useCallback((event: MouseEvent, start: ResizeStart) => {
    pdfSize.value = {
      width: Math.max(40, Math.min(95, start.width + ((event.clientX - start.x) / window.innerWidth) * 100)),
      height: Math.max(30, Math.min(95, start.height + ((event.clientY - start.y) / window.innerHeight) * 100))
    };
  }, []);

  const { isDragging, isResizing, handleDragMouseDown, handleResizeMouseDown } = useDraggableResizable({
    getDragStart: (event) => ({ x: event.clientX - pdfPosition.value.x, y: event.clientY - pdfPosition.value.y }),
    getResizeStart: (event) => ({
      x: event.clientX,
      y: event.clientY,
      width: pdfSize.value.width,
      height: pdfSize.value.height
    }),
    onDragMove: handleDragMove,
    onResizeMove: handleResizeMove,
    onResizeStart: (event) => event.stopPropagation(),
    isDragDisabled: pdfIsFullscreen.value || isMobile,
    isResizeDisabled: pdfIsFullscreen.value || isMobile
  });

  if (!pdfUrl) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      maxWidth="lg"
      fullWidth
      fullScreen={pdfIsFullscreen.value}
      onKeyDown={handleKeyPress}
      hideBackdrop={!isMobile}
      disableEscapeKeyDown={false}
      disableEnforceFocus={!isMobile}
      disableAutoFocus={!isMobile}
      disableRestoreFocus={!isMobile}
      disableScrollLock={!isMobile}
      sx={{
        pointerEvents: isMobile ? 'auto' : 'none',
        '& .MuiDialog-paper': {
          pointerEvents: 'auto',
          height: pdfIsFullscreen.value ? '100vh' : isMobile ? '60vh' : `${pdfSize.value.height}vh`,
          width: pdfIsFullscreen.value ? '100vw' : isMobile ? '90vw' : `${pdfSize.value.width}vw`,
          maxWidth: pdfIsFullscreen.value ? '100vw' : isMobile ? '90vw' : `${pdfSize.value.width}vw`,
          position: 'fixed',
          top: pdfIsFullscreen.value ? 0 : isMobile ? '50%' : `${pdfPosition.value.y}px`,
          left: pdfIsFullscreen.value ? 0 : isMobile ? '50%' : `${pdfPosition.value.x}px`,
          transform: isMobile && !pdfIsFullscreen.value ? 'translate(-50%, -50%)' : 'none',
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 24,
          zIndex: 1300,
          cursor: isDragging ? 'grabbing' : 'default'
        }
      }}
    >
      <DialogTitle
        onMouseDown={isMobile ? undefined : handleDragMouseDown}
        onClick={(e) => e.stopPropagation()}
        sx={{
          cursor: pdfIsFullscreen.value || isMobile ? 'default' : 'grab',
          '&:active': { cursor: pdfIsFullscreen.value || isMobile ? 'default' : 'grabbing' }
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Scenario Documentation</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Reset to Default Size & Zoom">
              <IconButton onClick={resetToDefault} color="inherit" disabled={isLoading || !!error}>
                <RestartAlt />
              </IconButton>
            </Tooltip>
            <Tooltip title="Toggle Fullscreen">
              <IconButton onClick={handleToggleFullscreen} color="inherit">
                {pdfIsFullscreen.value ? <FullscreenExit /> : <Fullscreen />}
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose} color="inherit">
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent
        ref={containerRef}
        onClick={(e) => e.stopPropagation()}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 1,
          overflow: 'auto',
          position: 'relative'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, gap: 1 }}>
          <Tooltip title="Previous Page (←)">
            <IconButton onClick={previousPage} disabled={pdfPageNumber.value <= 1 || isLoading || !!error}>
              <NavigateBefore />
            </IconButton>
          </Tooltip>
          <Typography variant="body2">
            Page {pdfPageNumber.value} of {pdfNumPages.value || '?'}
          </Typography>
          <Tooltip title="Next Page (→)">
            <IconButton
              onClick={nextPage}
              disabled={pdfPageNumber.value >= (pdfNumPages.value || 1) || isLoading || !!error}
            >
              <NavigateNext />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom Out (-)">
            <IconButton onClick={zoomOut} disabled={pdfScale.value <= 0.5 || isLoading || !!error}>
              <ZoomOut />
            </IconButton>
          </Tooltip>
          <Typography variant="body2">{Math.round(pdfScale.value * 100)}%</Typography>
          <Tooltip title="Zoom In (+)">
            <IconButton onClick={zoomIn} disabled={pdfScale.value >= 3.0 || isLoading || !!error}>
              <ZoomIn />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reset Zoom (100%)">
            <IconButton onClick={resetZoomOnly} disabled={isLoading || !!error}>
              <CenterFocusStrong />
            </IconButton>
          </Tooltip>
        </Box>
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            justifyContent: pdfScale.value > 1.2 ? 'flex-start' : 'center',
            alignItems: 'flex-start',
            minHeight: 0
          }}
        >
          {error ? (
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}
            >
              <Typography color="error" variant="h6">
                {error}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                minWidth: pdfScale.value > 1.2 ? `${pdfScale.value * 100}%` : '100%',
                width: pdfScale.value > 1.2 ? 'max-content' : '100%',
                padding: pdfScale.value > 1.2 ? '10px' : '0'
              }}
            >
              <Document
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
                    <Typography>Loading PDF...</Typography>
                  </Box>
                }
              >
                <Page
                  pageNumber={pdfPageNumber.value}
                  scale={pdfScale.value}
                  width={
                    pdfIsFullscreen.value
                      ? window.innerWidth - 40
                      : isMobile
                      ? window.innerWidth * 0.85
                      : (pdfSize.value.width / 100) * window.innerWidth - 40
                  }
                  loading={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
                      <Typography>Loading page...</Typography>
                    </Box>
                  }
                  error={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
                      <Typography color="error">Error loading page</Typography>
                    </Box>
                  }
                />
              </Document>
            </Box>
          )}
        </Box>
        {!pdfIsFullscreen.value && !isMobile && (
          <Box
            onMouseDown={handleResizeMouseDown}
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: 20,
              height: 20,
              cursor: 'se-resize',
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.2)'
              }
            }}
          />
        )}

        {/* Mobile fullscreen exit button */}
        {isMobile && pdfIsFullscreen.value && (
          <IconButton
            onClick={handleToggleFullscreen}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.8)'
              },
              zIndex: 1301
            }}
          >
            <FullscreenExit />
          </IconButton>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PDFOverlay;
